/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { AmlRequest, BatchCallRequest, BatchResponse } from 'src/core/types/azureMachineLearningTypes';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';

export const getBatchService = createService(({ context, getEnvironment }) => {
    const { authWebApiUrl, isProduction } = getEnvironment();
    const url = urlBuilder()
        .baseUrl(authWebApiUrl)
        .resource('batch')
        .build();

    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    const createSinglePost = async <T, k>({
        content,
        headers,
        requestUrl
    }: {
        content: T;
        headers: AmlRequest<T>['Headers'];
        requestUrl: string;
    }) => {
        const createObject: BatchCallRequest<typeof content> = {
            HttpMethod: 'POST',
            Url: requestUrl,
            Content: content
        };
        const requestObject: AmlRequest<BatchCallRequest<typeof content>> = {
            Headers: headers,
            Requests: [createObject]
        };

        try {
            const { data } = await httpService.post<BatchResponse<k>[]>(url, requestObject);
            const result = data[0];
            if (result?.error) {
                throw result.error;
            }

            return result;
        } catch (error) {
            throw error;
        }
    };

    const createSingleGet = async <T, k>({ headers, requestUrl }: { headers: AmlRequest<T>['Headers']; requestUrl: string }) => {
        const createObject: BatchCallRequest<T> = {
            HttpMethod: 'GET',
            Url: requestUrl
        };
        const requestObject: AmlRequest<BatchCallRequest<T>> = {
            Headers: headers,
            Requests: [createObject]
        };

        try {
            const { data } = await httpService.post<BatchResponse<k>[]>(url, requestObject);
            const result = data[0];
            if (result.error) {
                throw result.error;
            }

            return result;
        } catch (error) {
            throw error;
        }
    };

    const createSinglePatch = async <T, k>({ headers, requestUrl }: { headers: AmlRequest<T>['Headers']; requestUrl: string }) => {
        const createObject: BatchCallRequest<T> = {
            HttpMethod: 'PATCH',
            Url: requestUrl
        };
        const requestObject: AmlRequest<BatchCallRequest<T>> = {
            Headers: headers,
            Requests: [createObject]
        };

        try {
            const { data } = await httpService.post<BatchResponse<k>[]>(url, requestObject);
            const result = data[0];
            if (result.error) {
                throw result.error;
            }

            return result;
        } catch (error) {
            throw error;
        }
    };

    const batchLongOperations = async <T, K>({
        headers,
        requestUrl,
        operationHandle
    }: {
        headers: AmlRequest<T>['Headers'];
        requestUrl: string;
        operationHandle: OperationHandler<BatchResponse<K>[]>;
    }) => {
        const createObject: BatchCallRequest<T> = {
            HttpMethod: 'GET',
            Url: requestUrl
        };
        const requestObject: AmlRequest<BatchCallRequest<T>> = {
            Headers: headers,
            Requests: [createObject]
        };

        const response = await createOperationCall(url, apiOptions, operationHandle, 0, () =>
            httpService.post<BatchResponse<K>[]>(url, requestObject)
        );
        return response.data[0];
    };

    return { createSinglePost, createSingleGet, createSinglePatch, batchLongOperations };
});
